import {
  DailyOpenHoursSettings,
  DaySettings,
  StepCompletionStatus,
  TimeRange,
} from '@/shared/types/availability';
import {
  parse,
  areIntervalsOverlapping,
  addMinutes,
  format,
  addDays,
} from 'date-fns';
import {
  DAYS_OF_WEEK,
  AvailabilityStep,
  Timezone,
  US_TIMEZONES,
} from '@/shared/constants/availability';
import {
  DateAvailabilitySlotInput,
  DayOfWeek,
  StylistNylasData,
} from '@/shared/types/generated';
import { OpenHoursItem } from '@/shared/types/availability';

let _localIdCounter = 0;
export function localId(): string {
  return `local-${Date.now()}-${++_localIdCounter}`;
}

export const validateDaySettings = (
  day: DayOfWeek,
  settings: DailyOpenHoursSettings,
): DaySettings => {
  const daySettings = settings[day] || { enabled: false, timeRanges: [] };

  return {
    enabled: daySettings.enabled ?? false,
    timeRanges: (daySettings.timeRanges || []).map((range) => ({
      id: range.id || localId(),
      startTime: range.startTime || '09:00',
      endTime: range.endTime || '17:00',
    })),
  };
};

export const createValidatedSettings = (
  settings: DailyOpenHoursSettings,
): DailyOpenHoursSettings => {
  const validatedSettings = {} as DailyOpenHoursSettings;

  // Validate each day's settings
  DAYS_OF_WEEK.forEach(({ key }) => {
    validatedSettings[key] = validateDaySettings(key, settings);
  });

  return validatedSettings;
};

/**
 * Derives which availability steps are complete from scheduling API response.
 */
export const getStepCompletionStatus = (
  data: StylistNylasData,
): StepCompletionStatus => {
  const { nylasConnections, scheduleConfigs, openHours } = data;
  const { bookingCalendarId, timezone, availabilityCalendarIds } =
    scheduleConfigs[0] || {};

  return {
    [AvailabilityStep.CalendarConnection]: !!(
      nylasConnections && nylasConnections.length > 0
    ),
    [AvailabilityStep.Availability]: !!(
      availabilityCalendarIds && availabilityCalendarIds.length > 0
    ),
    [AvailabilityStep.Booking]: !!(bookingCalendarId != null),
    [AvailabilityStep.TimeZoneSelection]: !!timezone,
    [AvailabilityStep.Schedule]:
      (!!openHours && openHours.length > 0) ||
      (!!data.availabilitySlots && data.availabilitySlots.length > 0),
  };
};

/**
 * Returns true when the user has completed calendar setup (steps 0–3): connection,
 * availability calendar selection, booking calendar, and timezone. Used on mobile
 * to decide whether to show the Schedule step only or the "go to web" message.
 */
export const isCalendarSetupComplete = (
  data: StylistNylasData | null | undefined,
): boolean => {
  if (!data?.scheduleConfigs?.length) {
    return false;
  }
  const status = getStepCompletionStatus(data);
  return (
    status[AvailabilityStep.CalendarConnection] &&
    status[AvailabilityStep.Availability] &&
    status[AvailabilityStep.Booking] &&
    status[AvailabilityStep.TimeZoneSelection]
  );
};

export const getCurrentStep = (
  completedSteps: AvailabilityStep[],
): AvailabilityStep => {
  const stepsInOrder = [
    AvailabilityStep.CalendarConnection,
    AvailabilityStep.Availability,
    AvailabilityStep.Booking,
    AvailabilityStep.TimeZoneSelection,
    AvailabilityStep.Schedule,
  ];

  const nextStep = stepsInOrder.find((step) => !completedSteps.includes(step));
  return nextStep !== undefined ? nextStep : AvailabilityStep.Schedule;
};

export function convertRawOpenHoursToSettings(
  raw: OpenHoursItem[],
): DailyOpenHoursSettings {
  // 1) build up a partial record of only the days that had raw data
  const settings: Partial<Record<DayOfWeek, DaySettings>> = {};

  raw.forEach(({ day, startTime, endTime }) => {
    const daySettings = settings[day] ?? { enabled: false, timeRanges: [] };
    // push the incoming range
    daySettings.timeRanges.push({ id: localId(), startTime, endTime });
    // now that we have at least one, mark it enabled
    daySettings.enabled = true;
    settings[day] = daySettings;
  });

  return DAYS_OF_WEEK.reduce((acc, { key: day }) => {
    const existing = settings[day];
    acc[day] = existing ?? {
      enabled: false,
      timeRanges: [{ id: localId(), startTime: '09:00', endTime: '17:00' }],
    };
    return acc;
  }, {} as DailyOpenHoursSettings);
}

export function convertSettingsToRawOpenHours(
  settings: DailyOpenHoursSettings,
): OpenHoursItem[] {
  return DAYS_OF_WEEK.flatMap(({ key: day }) =>
    settings[day].enabled
      ? settings[day].timeRanges.map(({ startTime, endTime }) => ({
          day,
          startTime,
          endTime,
        }))
      : [],
  );
}

/**
 * Return true if no two time‐ranges overlap.
 */
export function validateTimeRangeOverlap(
  ranges: Array<{ startTime: string; endTime: string }>,
): boolean {
  if (ranges.length < 2) return true;

  // Turn them all into sorted Date intervals
  const intervals = ranges
    .map((range) => toInterval(range.startTime, range.endTime))
    .sort((a, b) => a.start.getTime() - b.start.getTime());

  // Check each adjacent pair for overlap
  for (let i = 0; i + 1 < intervals.length; i++) {
    if (
      areIntervalsOverlapping(intervals[i], intervals[i + 1], {
        inclusive: false,
      })
    ) {
      return false;
    }
  }

  return true;
}

/**
 * Given lastEnd, return the next HH:mm slot of `lengthMinutes`.
 * If adding would wrap past midnight, jump to the first post-midnight slot.
 */
export function getNextTimeRange(
  lastEnd: string,
  { lengthMinutes = 60 }: { lengthMinutes?: number } = {},
): { startTime: string; endTime: string } | null {
  const today = new Date();
  const startDate = parse(lastEnd, 'HH:mm', today);
  const tentativeEnd = addMinutes(startDate, lengthMinutes);

  // Slot fits entirely within the same day (no midnight crossing)
  if (tentativeEnd.getDate() === startDate.getDate()) {
    return {
      startTime: format(startDate, 'HH:mm'),
      endTime: format(tentativeEnd, 'HH:mm'),
    };
  }

  // wrapped past midnight -> skip to 00:00 -> length
  const midnight = new Date(tentativeEnd);
  const postMidnight = addMinutes(midnight, lengthMinutes);

  return {
    startTime: format(midnight, 'HH:mm'),
    endTime: format(postMidnight, 'HH:mm'),
  };
}

/**
 * Turn an HH:mm pair into a Date‐interval;
 * if end ≤ start, roll end into tomorrow.
 */
export function toInterval(
  startTime: string,
  endTime: string,
): { start: Date; end: Date } {
  const today = new Date();
  const tomorrow = addDays(today, 1);

  const start = parse(startTime, 'HH:mm', today);
  let end = parse(endTime, 'HH:mm', today);

  if (end <= start) {
    end = parse(endTime, 'HH:mm', tomorrow);
  }

  return { start, end };
}

export function hhmmToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

export function sortByStartTime(ranges: TimeRange[]) {
  return [...ranges].sort(
    (a, b) => hhmmToMinutes(a.startTime) - hhmmToMinutes(b.startTime),
  );
}

/**
 * Merges adjacent 30-minute slot selections into contiguous time ranges.
 * Slots must share the same date; assumes slots are already sorted by startTime.
 * Example: [{9:00-9:30}, {9:30-10:00}, {10:00-10:30}] -> [{9:00-10:30}]
 */
export function mergeAdjacentSlots(
  slots: DateAvailabilitySlotInput[],
): DateAvailabilitySlotInput[] {
  if (slots.length === 0) {
    return [];
  }

  const sorted = [...slots].sort(
    (a, b) => hhmmToMinutes(a.startTime) - hhmmToMinutes(b.startTime),
  );

  const merged: DateAvailabilitySlotInput[] = [];
  let current = { ...sorted[0] };

  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].startTime === current.endTime) {
      current.endTime = sorted[i].endTime;
    } else {
      merged.push(current);
      current = { ...sorted[i] };
    }
  }
  merged.push(current);

  return merged;
}

export function getDefaultTimezone() {
  try {
    const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return US_TIMEZONES.some((tz) => tz.value === detected)
      ? detected
      : Timezone.Eastern;
  } catch (error) {
    __DEV__ && console.error('Error detecting timezone:', error);
    return Timezone.Eastern;
  }
}
